import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { http, imageBaseUrl } from '../../config/http';
import { useNavigate } from 'react-router-dom';
import Layouts from '../../layouts/Layouts';
import useCabang from '../features/useCabang';
import SuratJalanPrint from '../../components/SuratJalanPrint';
import moment from 'moment';
import 'moment/locale/id';
import { Dialog, Transition } from '@headlessui/react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import {
  IconTruckDelivery,
  IconCheck,
  IconX,
  IconPrinter,
  IconSearch,
  IconEye,
  IconChartPie,
  IconListDetails,
  IconUser,
  IconPhoto,
  IconBuildingStore,
  IconClock,
  IconCircleCheck,
  IconInbox,
  IconLoader2
} from '@tabler/icons-react';
import { toast } from 'react-toastify';

const HistoriPengiriman = () => {
  const navigate = useNavigate();
  const { cabang } = useCabang();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = (localStorage.getItem('role') || user.role || 'kasir').toLowerCase().trim();

  useEffect(() => {
    if (userRole === 'operator_rumah') {
      toast.error('Akses ditolak: Operator rumah tidak diizinkan mengakses menu pengiriman');
      navigate('/');
    }
  }, [userRole, navigate]);

  const [pengirimans, setPengirimans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [cabangFilter, setCabangFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  // Detail & Print Modal State
  const [selectedPengiriman, setSelectedPengiriman] = useState(null);
  const [selectedBreakdown, setSelectedBreakdown] = useState([]);
  const [isOpenPrint, setIsOpenPrint] = useState(false);
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [modalTab, setModalTab] = useState('ringkasan');
  const [searchModalItem, setSearchModalItem] = useState('');

  // Validation Modal State
  const [isOpenValidationModal, setIsOpenValidationModal] = useState(false);
  const [validatingPengiriman, setValidatingPengiriman] = useState(null);
  const [validationAction, setValidationAction] = useState('diterima');
  const [catatanPenerimaan, setCatatanPenerimaan] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const fetchPengrimanList = async () => {
    setLoading(true);
    try {
      let url = `/pengiriman?page=${page}&per_page=10`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (cabangFilter) url += `&cabang_tujuan_id=${cabangFilter}`;
      if (search) url += `&search=${search}`;

      const res = await http.get(url);
      if (res.data.status) {
        setPengirimans(res.data.data.data || []);
        setLastPage(res.data.data.last_page || 1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPengrimanList();
  }, [page, statusFilter, cabangFilter, search]);

  const handleOpenDetail = async (id, itemPreload = null) => {
    if (itemPreload) {
      setSelectedPengiriman(itemPreload);
      setLoadingDetail(!itemPreload.items || itemPreload.items.length === 0);
    } else {
      setLoadingDetail(true);
    }
    setIsOpenDetailModal(true);
    setModalTab('ringkasan');
    setSearchModalItem('');

    try {
      const res = await http.get(`/pengiriman/${id}`);
      if (res.data.status) {
        setSelectedPengiriman(res.data.data);
        setSelectedBreakdown(res.data.breakdown_kategori || []);
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memuat detail Surat Jalan');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleOpenPrint = async (id) => {
    try {
      const res = await http.get(`/pengiriman/${id}`);
      if (res.data.status) {
        setSelectedPengiriman(res.data.data);
        setSelectedBreakdown(res.data.breakdown_kategori || []);
        setIsOpenPrint(true);
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal menyiapkan dokumen cetak');
    }
  };

  const handleOpenValidation = async (pengiriman) => {
    try {
      const res = await http.get(`/pengiriman/${pengiriman.id}`);
      if (res.data.status) {
        setValidatingPengiriman(res.data.data);
        setValidationAction('diterima');
        setCatatanPenerimaan('');
        setIsOpenValidationModal(true);
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memuat data validasi');
    }
  };

  const handleSubmitValidation = async (e) => {
    e.preventDefault();
    if (!validatingPengiriman) return;

    if (validationAction === 'ditolak' && !catatanPenerimaan.trim()) {
      toast.warn('Silakan tuliskan alasan penolakan/selisih barang!');
      return;
    }

    setIsValidating(true);
    try {
      const res = await http.post(`/pengiriman/${validatingPengiriman.id}/validasi`, {
        action: validationAction,
        catatan_penerimaan: catatanPenerimaan
      });

      if (res.data.status) {
        toast.success(res.data.message);
        setIsOpenValidationModal(false);
        if (selectedPengiriman && selectedPengiriman.id === validatingPengiriman.id) {
          handleOpenDetail(validatingPengiriman.id);
        }
        fetchPengrimanList();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Gagal memproses validasi');
    } finally {
      setIsValidating(false);
    }
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'diterima':
        return (
          <span className="px-2.5 py-1 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 rounded-lg text-xs font-bold flex items-center gap-1 w-fit">
            <IconCheck size={14} /> Diterima Toko
          </span>
        );
      case 'ditolak':
        return (
          <span className="px-2.5 py-1 bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 border border-rose-300 dark:border-rose-700 rounded-lg text-xs font-bold flex items-center gap-1 w-fit">
            <IconX size={14} /> Ditolak / Ditunda
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-1 bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700 rounded-lg text-xs font-bold flex items-center gap-1 animate-pulse w-fit">
            <IconTruckDelivery size={14} /> Dalam Pengiriman
          </span>
        );
    }
  };

  const resolveItemImageUrl = (item) => {
    if (!item) return '';
    let gData = item.gambar1 || item.gambar;
    if (typeof gData === 'string' && (gData.startsWith('[') || gData.startsWith('{'))) {
      try {
        gData = JSON.parse(gData);
      } catch (e) {}
    }
    if (Array.isArray(gData) && gData.length > 0) {
      const path = gData[0]?.asli || gData[0]?.thumbnail || (typeof gData[0] === 'string' ? gData[0] : '');
      return path ? (path.startsWith('http') ? path : `${imageBaseUrl}${path}`) : '';
    }
    if (typeof gData === 'string' && gData.length > 0) {
      return gData.startsWith('http') ? gData : `${imageBaseUrl}${gData}`;
    }
    return '';
  };

  const categoryKaratBreakdown = useMemo(() => {
    if (!selectedPengiriman?.items || selectedPengiriman.items.length === 0) {
      if (selectedBreakdown && selectedBreakdown.length > 0) {
        return selectedBreakdown.map((b) => ({
          name: b.nama_kategori,
          totalPcs: b.total_pcs,
          totalBerat: b.total_berat,
          karats: Object.entries(b.jenis_list || {}).reduce((acc, [jenis, val]) => {
            acc[jenis] = { pcs: val.pcs, berat: val.berat };
            return acc;
          }, {})
        }));
      }
      return [];
    }
    const map = {};
    selectedPengiriman.items.forEach((item) => {
      const catName =
        item.jenis_kategori?.kategori?.nama_kategori ||
        item.kategori?.nama_kategori ||
        item.jenis_kategori?.nama_jenis ||
        item.kategori ||
        'Lainnya';
      const karat = item.karat ? `${item.karat} K` : 'Non';
      const berat = parseFloat(item.berat || 0);

      if (!map[catName]) {
        map[catName] = { name: catName, totalPcs: 0, totalBerat: 0, karats: {} };
      }
      map[catName].totalPcs += 1;
      map[catName].totalBerat += berat;

      if (!map[catName].karats[karat]) {
        map[catName].karats[karat] = { pcs: 0, berat: 0 };
      }
      map[catName].karats[karat].pcs += 1;
      map[catName].karats[karat].berat += berat;
    });
    return Object.values(map);
  }, [selectedPengiriman, selectedBreakdown]);

  const filteredModalItems = useMemo(() => {
    if (!selectedPengiriman || !selectedPengiriman.items) return [];
    if (!searchModalItem.trim()) return selectedPengiriman.items;
    const q = searchModalItem.toLowerCase();
    return selectedPengiriman.items.filter((it) => {
      const kode = it.kode_item ? it.kode_item.toLowerCase() : '';
      const jenis = it.jenis_kategori?.nama_jenis ? it.jenis_kategori.nama_jenis.toLowerCase() : '';
      const kat = it.jenis_kategori?.kategori?.nama_kategori ? it.jenis_kategori.kategori.nama_kategori.toLowerCase() : '';
      const baki = it.nomor_rak ? String(it.nomor_rak).toLowerCase() : '';
      return kode.includes(q) || jenis.includes(q) || kat.includes(q) || baki.includes(q);
    });
  }, [selectedPengiriman, searchModalItem]);

  return (
    <Layouts title="Histori Surat Jalan & Pengiriman Barang">
      <div className="p-4 sm:p-6 w-full space-y-6">

        {/* Filter Controls */}
        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
            {/* Filter Status */}
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 text-xs border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">-- Semua Status --</option>
              <option value="dalam_pengiriman">Dalam Pengiriman</option>
              <option value="diterima">Diterima Toko</option>
              <option value="ditolak">Ditolak / Ditunda</option>
            </select>

            {/* Filter Cabang */}
            <select
              value={cabangFilter}
              onChange={(e) => { setCabangFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 text-xs border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">-- Semua Toko Tujuan --</option>
              {cabang.filter(c => c.is_toko !== false).map(c => (
                <option key={c.id} value={c.id}>{c.nama_cabang}</option>
              ))}
            </select>
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-64">
            <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari No. SJ / Kurir..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 text-xs border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-x-auto">
          {loading ? (
            <div className="py-16 text-center text-slate-400 text-xs font-medium">Memuat data pengiriman...</div>
          ) : pengirimans.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-xs font-medium">Belum ada dokumen pengiriman yang sesuai filter.</div>
          ) : (
            <table className="w-full text-left text-xs text-slate-600 dark:text-slate-300">
              <thead className="text-[11px] uppercase bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="p-3.5 text-center w-12">No.</th>
                  <th className="p-3.5">No. Surat Jalan</th>
                  <th className="p-3.5">Tanggal (Dibuat & Diterima)</th>
                  <th className="p-3.5">Asal - Tujuan</th>
                  <th className="p-3.5">Kurir & Pemberi</th>
                  <th className="p-3.5 text-center">Jumlah Barang</th>
                  <th className="p-3.5">Status Pengiriman</th>
                  <th className="p-3.5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {pengirimans.map((p, index) => (
                  <tr
                    key={p.id}
                    onClick={() => handleOpenDetail(p.id, p)}
                    className="hover:bg-amber-50/50 dark:hover:bg-slate-700/40 transition-colors cursor-pointer group"
                    title="Klik baris untuk melihat detail surat jalan"
                  >
                    <td className="p-3.5 text-center font-semibold text-slate-500">
                      {(page - 1) * 10 + index + 1}
                    </td>
                    <td className="p-3.5">
                      <span className="font-mono font-bold text-amber-600 dark:text-amber-400 group-hover:underline">
                        {p.no_surat_jalan}
                      </span>
                    </td>
                    <td className="p-3.5">
                      <div className="flex flex-col gap-1">
                        <div className="text-xs text-slate-600 dark:text-slate-300">
                          <span className="font-semibold text-slate-400 dark:text-slate-500">Dibuat:</span>{' '}
                          {moment(p.tanggal).format('DD/MM/YYYY HH:mm')}
                        </div>
                        {p.waktu_diterima ? (
                          <div className="text-xs text-emerald-600 dark:text-emerald-400">
                            <span className="font-semibold">Diterima:</span>{' '}
                            {moment(p.waktu_diterima).format('DD/MM/YYYY HH:mm')}
                          </div>
                        ) : (
                          <div className="text-[10px] text-amber-500 italic">Belum Diterima</div>
                        )}
                      </div>
                    </td>
                    <td className="p-3.5">
                      <span className="font-semibold text-slate-700 dark:text-slate-200">{p.cabang_asal?.nama_cabang || 'Brankas Pusat'}</span>
                      <span className="text-amber-500 mx-1.5 font-bold">➔</span>
                      <span className="font-bold text-amber-700 dark:text-amber-400">{p.cabang_tujuan?.nama_cabang}</span>
                    </td>
                    <td className="p-3.5">
                      <div className="font-semibold text-slate-800 dark:text-slate-200">Kurir: {p.nama_kurir}</div>
                      <div className="text-[10px] text-slate-400">Pemberi: {p.pemberi_user?.name || '-'}</div>
                    </td>
                    <td className="p-3.5 text-center">
                      <span className="font-bold text-slate-900 dark:text-slate-100">{p.total_pcs} pcs</span>
                      <span className="text-slate-400 block text-[10px]">({parseFloat(p.total_berat).toFixed(3)}g)</span>
                    </td>
                    <td className="p-3.5">{renderStatusBadge(p.status)}</td>
                    <td className="p-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1.5">
                        {/* Tombol Print */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenPrint(p.id);
                          }}
                          className="p-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-all"
                          title="Cetak Surat Jalan"
                        >
                          <IconPrinter size={16} />
                        </button>

                        {/* Tombol Detail */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDetail(p.id, p);
                          }}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-all"
                          title="Lihat Detail"
                        >
                          <IconEye size={16} />
                        </button>

                        {/* Tombol Validasi (Jika role kasir/operator & status dalam_pengiriman) */}
                        {p.status === 'dalam_pengiriman' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenValidation(p);
                            }}
                            className="px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-[11px] rounded-lg shadow transition-all flex items-center gap-1 cursor-pointer"
                          >
                            <IconCheck size={14} />
                            <span>Validasi Toko</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {lastPage > 1 && (
          <div className="flex justify-center items-center gap-1.5 pt-4 pb-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
            >
              Prev
            </button>
            
            <div className="flex items-center gap-1 mx-2">
              {Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
                let pageNum;
                if (lastPage <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= lastPage - 2) {
                  pageNum = lastPage - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center text-xs font-bold rounded-lg border transition-colors cursor-pointer ${
                      page === pageNum
                        ? 'bg-amber-500 border-amber-500 text-white shadow-md shadow-amber-500/20'
                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              disabled={page === lastPage}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
            >
              Next
            </button>
          </div>
        )}

        {/* Modal Validasi Penerimaan Toko */}
        {isOpenValidationModal && validatingPengiriman && createPortal(
          <div className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 w-full max-w-lg rounded-2xl p-6 shadow-2xl space-y-4 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3">
                <h3 className="font-bold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                  <IconCheck className="text-amber-500" size={18} />
                  <span>Validasi Serah Terima Surat Jalan</span>
                </h3>
                <button onClick={() => setIsOpenValidationModal(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                  <IconX size={18} />
                </button>
              </div>

              <div className="bg-amber-50/60 dark:bg-amber-900/20 p-3 rounded-xl border border-amber-200 dark:border-amber-700/50 text-xs space-y-1">
                <p><strong>No. Surat Jalan:</strong> <span className="font-mono text-amber-800 dark:text-amber-300">{validatingPengiriman.no_surat_jalan}</span></p>
                <p><strong>Toko Tujuan:</strong> {validatingPengiriman.cabang_tujuan?.nama_cabang}</p>
                <p><strong>Kurir Pengantar:</strong> {validatingPengiriman.nama_kurir}</p>
                <p><strong>Total Barang:</strong> {validatingPengiriman.total_pcs} Pcs ({parseFloat(validatingPengiriman.total_berat).toFixed(3)} gr)</p>
                <p className="pt-1 mt-1 border-t border-amber-200/50 dark:border-amber-700/30 text-amber-800 dark:text-amber-400">
                  <strong>Penerima / Validator:</strong> {user.name || 'Akun Saat Ini'} (Otomatis)
                </p>
              </div>

              <form onSubmit={handleSubmitValidation} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">
                    Hasil Pencocokan Fisik vs Sistem <span className="text-rose-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                      validationAction === 'diterima'
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 font-bold'
                        : 'border-slate-200 dark:border-slate-700 text-slate-600'
                    }`}>
                      <input
                        type="radio"
                        name="action"
                        value="diterima"
                        checked={validationAction === 'diterima'}
                        onChange={() => setValidationAction('diterima')}
                      />
                      <span>Fisik Cocok (Terima & Masuk Etalase)</span>
                    </label>

                    <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                      validationAction === 'ditolak'
                        ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300 font-bold'
                        : 'border-slate-200 dark:border-slate-700 text-slate-600'
                    }`}>
                      <input
                        type="radio"
                        name="action"
                        value="ditolak"
                        checked={validationAction === 'ditolak'}
                        onChange={() => setValidationAction('ditolak')}
                      />
                      <span>Ada Selisih / Tolak</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">
                    Catatan Penerima / Keterangan Selisih
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Tuliskan catatan kondisi fisik atau alasan jika ada selisih/penolakan..."
                    value={catatanPenerimaan}
                    onChange={(e) => setCatatanPenerimaan(e.target.value)}
                    className="w-full p-2.5 text-xs border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => setIsOpenValidationModal(false)}
                    className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all cursor-pointer"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    disabled={isValidating}
                    className={`px-5 py-2 text-xs font-bold text-white rounded-xl shadow-lg transition-all cursor-pointer ${
                      validationAction === 'diterima'
                        ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20'
                        : 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/20'
                    }`}
                  >
                    {isValidating ? 'Memproses...' : 'Simpan & Selesaikan Validasi'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        , document.body)}

        {/* Modal Detail Surat Jalan / Pengiriman (5 Tab Structure - Seragam dengan VerifikasiTransferToko) */}
        <Transition appear show={isOpenDetailModal} as={Fragment}>
          <Dialog as="div" className="relative z-[99999]" onClose={() => setIsOpenDetailModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-4xl lg:max-w-5xl transform overflow-hidden rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700 text-left align-middle transition-all max-h-[92vh] flex flex-col space-y-4 p-5 md:p-6">
                    {loadingDetail && !selectedPengiriman ? (
                      <div className="p-16 flex flex-col items-center justify-center">
                        <IconLoader2 size={36} className="animate-spin text-amber-500" />
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2.5 font-medium">Memuat detail Surat Jalan...</p>
                      </div>
                    ) : selectedPengiriman && (
                      <>
                        {/* Modal Header */}
                        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3 flex-shrink-0">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Dialog.Title as="h3" className="font-bold text-sm md:text-base text-slate-800 dark:text-slate-100">
                                Detail Dokumen: <span className="font-mono text-amber-600 dark:text-amber-400">{selectedPengiriman.no_surat_jalan}</span>
                              </Dialog.Title>
                              {selectedPengiriman.status === 'diterima' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 rounded-md border border-emerald-300 dark:border-emerald-700 text-[11px] font-bold">
                                  ✓ Diterima Toko
                                </span>
                              ) : selectedPengiriman.status === 'ditolak' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 rounded-md border border-rose-300 dark:border-rose-700 text-[11px] font-bold">
                                  ✕ Ditolak / Ditunda
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 rounded-md border border-amber-300 dark:border-amber-700 text-[11px] font-bold animate-pulse">
                                  ● Dalam Pengiriman
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                              <span>
                                Dibuat oleh <strong className="text-slate-700 dark:text-slate-200">{selectedPengiriman.pemberi_user?.name || selectedPengiriman.pemberiUser?.name || 'Operator'}</strong> pada {moment(selectedPengiriman.tanggal || selectedPengiriman.created_at).locale('id').format('DD MMMM YYYY HH:mm')}
                              </span>
                              {selectedPengiriman.waktu_diterima && (
                                <span>
                                  • <strong className="text-slate-600 dark:text-slate-300">Diterima:</strong>{' '}
                                  {moment(selectedPengiriman.waktu_diterima).locale('id').format('DD MMMM YYYY HH:mm')}
                                  {(selectedPengiriman.penerima_user?.name || selectedPengiriman.penerimaUser?.name) && (
                                    <> oleh <strong className="text-emerald-600 dark:text-emerald-400">{selectedPengiriman.penerima_user?.name || selectedPengiriman.penerimaUser?.name}</strong></>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setIsOpenDetailModal(false)}
                            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
                          >
                            <IconX size={20} />
                          </button>
                        </div>

                        {/* Top Quick Info Strip (5 Kolom) */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-xs bg-slate-50 dark:bg-slate-700/40 p-3 rounded-xl flex-shrink-0 border border-slate-200/60 dark:border-slate-700/60">
                          <div>
                            <span className="text-slate-400 block text-[10px]">Toko / Cabang Asal</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200 truncate block mt-0.5">
                              {selectedPengiriman.cabang_asal?.nama_cabang || 'Brankas Pusat'}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Toko Tujuan</span>
                            <span className="font-bold text-amber-600 dark:text-amber-400 truncate block mt-0.5">
                              {selectedPengiriman.cabang_tujuan?.nama_cabang || '-'}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Kurir & Pembuat</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200 truncate block mt-0.5">
                              {selectedPengiriman.nama_kurir || '-'}
                              <span className="text-[10px] text-slate-400 block font-normal truncate">
                                Oleh: {selectedPengiriman.pemberi_user?.name || selectedPengiriman.pemberiUser?.name || '-'}
                              </span>
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Total Item & Berat</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200 truncate block mt-0.5">
                              {selectedPengiriman.total_pcs || (selectedPengiriman.items ? selectedPengiriman.items.length : 0)} PCS
                              <span className="text-amber-600 dark:text-amber-400 font-semibold ml-1">
                                ({parseFloat(selectedPengiriman.total_berat || (selectedPengiriman.items ? selectedPengiriman.items.reduce((acc, it) => acc + parseFloat(it.berat || 0), 0) : 0)).toFixed(3)} gr)
                              </span>
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Penerima / Validator</span>
                            <span className={`font-bold truncate block mt-0.5 ${
                              selectedPengiriman.status === 'diterima'
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : selectedPengiriman.status === 'ditolak'
                                ? 'text-rose-600 dark:text-rose-400'
                                : 'text-amber-600 dark:text-amber-400'
                            }`}>
                              {selectedPengiriman.penerima_user?.name || selectedPengiriman.penerimaUser?.name || (
                                selectedPengiriman.status === 'dalam_pengiriman' ? 'Menunggu Penerimaan...' : '-'
                              )}
                            </span>
                          </div>
                        </div>

                        {/* 5 Tab Navigation Bar */}
                        <div className="flex border-b border-slate-200 dark:border-slate-700 gap-1 overflow-x-auto scrollbar-none flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => setModalTab('ringkasan')}
                            className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                              modalTab === 'ringkasan'
                                ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                          >
                            <IconChartPie size={16} />
                            <span>Ringkasan Item</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setModalTab('list')}
                            className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                              modalTab === 'list'
                                ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                          >
                            <IconListDetails size={16} />
                            <span>List Item ({selectedPengiriman.items?.length || selectedPengiriman.total_pcs || 0})</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setModalTab('operator')}
                            className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                              modalTab === 'operator'
                                ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                          >
                            <IconUser size={16} />
                            <span>Operator / Pembuat Pengiriman</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setModalTab('verifikator')}
                            className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                              modalTab === 'verifikator'
                                ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                          >
                            <IconCircleCheck size={16} />
                            <span>Penerima / Validasi Toko</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setModalTab('galeri')}
                            className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                              modalTab === 'galeri'
                                ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                          >
                            <IconPhoto size={16} />
                            <span>Galeri List Item</span>
                          </button>
                        </div>

                        {/* Scrollable Tab Content */}
                        <div className="flex-1 overflow-y-auto pr-1">
                          {/* 1. Tab Ringkasan Item */}
                          {modalTab === 'ringkasan' && (() => {
                            const itemsList = selectedPengiriman.items || [];
                            const totalPcsVal = selectedPengiriman.total_pcs || itemsList.length;
                            const totalBeratVal = parseFloat(selectedPengiriman.total_berat || itemsList.reduce((acc, it) => acc + parseFloat(it.berat || it.item?.berat || 0), 0));
                            const avgBeratVal = itemsList.length > 0 ? (totalBeratVal / itemsList.length) : (totalPcsVal > 0 ? totalBeratVal / totalPcsVal : 0);
                            const validModalItems = itemsList.filter(it => Number(it.harga_modal || it.item?.harga_modal || 0) > 0);
                            const avgModalVal = validModalItems.length > 0
                              ? Math.round(validModalItems.reduce((acc, it) => acc + Number(it.harga_modal || it.item?.harga_modal || 0), 0) / validModalItems.length)
                              : 0;
                            const maxBeratVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => parseFloat(it.berat || it.item?.berat || 0) || 0)) : 0;
                            const maxModalVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => Number(it.harga_modal || it.item?.harga_modal || 0) || 0)) : 0;

                            return (
                              <div className="space-y-4">
                                {/* Ringkasan Cards (7 Cards) */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
                                  <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                                    <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Barang</span>
                                    <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                                      {totalPcsVal} <span className="text-xs font-semibold text-slate-500">Pcs</span>
                                    </div>
                                  </div>
                                  <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                                    <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Berat</span>
                                    <div className="text-lg font-extrabold text-amber-600 dark:text-amber-400 mt-0.5 font-mono">
                                      {totalBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                    </div>
                                  </div>
                                  <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                    <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Total Kategori</span>
                                    <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                                      {categoryKaratBreakdown.length} <span className="text-xs font-semibold text-slate-500">Kategori</span>
                                    </div>
                                  </div>
                                  <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                    <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Berat</span>
                                    <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                      {avgBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                    </div>
                                  </div>
                                  <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                    <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Modal</span>
                                    <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                      Rp {avgModalVal.toLocaleString('id-ID')}
                                    </div>
                                  </div>
                                  <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                    <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Berat Maksimal</span>
                                    <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                      {maxBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                    </div>
                                  </div>
                                  <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                    <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Modal Maksimal</span>
                                    <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                      Rp {maxModalVal.toLocaleString('id-ID')}
                                    </div>
                                  </div>
                                </div>

                              {/* Breakdown Kategori & Karat */}
                              {categoryKaratBreakdown.length > 0 && (
                                <div className="space-y-3 p-3.5 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/30 rounded-xl">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-[11px] font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                                      <IconChartPie size={16} />
                                      <span>Breakdown Per Kategori & Karat</span>
                                    </h4>
                                    <span className="text-[10px] text-amber-700 dark:text-amber-400 font-semibold">
                                      {categoryKaratBreakdown.length} Kategori
                                    </span>
                                  </div>
                                  <div className="space-y-2">
                                    {categoryKaratBreakdown.map((cat, idx) => (
                                      <div key={idx} className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-amber-200/60 dark:border-slate-700 shadow-sm">
                                        <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                                          <span className="font-bold text-slate-800 dark:text-slate-100 text-sm">{cat.name}</span>
                                          <span className="font-bold text-amber-600 dark:text-amber-400 text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2.5 py-0.5 rounded-md">
                                            {cat.totalPcs} Pcs ({parseFloat(cat.totalBerat).toFixed(3)} gr)
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                          {Object.entries(cat.karats).map(([karat, val], i) => (
                                            <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/40 px-2.5 py-1.5 rounded-lg text-xs border border-slate-100 dark:border-slate-700">
                                              <span className="font-semibold text-slate-500 dark:text-slate-400">{karat}</span>
                                              <span className="font-bold text-slate-800 dark:text-slate-100">{val.pcs} Pcs / {parseFloat(val.berat).toFixed(3)}g</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Catatan Area */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                                  <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Catatan Pembuat / Pengiriman</span>
                                  <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                                    {selectedPengiriman.catatan_pengiriman || <span className="italic text-slate-400">Tidak ada catatan pengiriman.</span>}
                                  </p>
                                </div>
                                <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                                  <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Catatan Penerimaan Toko</span>
                                  <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                                    {selectedPengiriman.catatan_penerimaan || (
                                      <span className="italic text-slate-400">
                                        {selectedPengiriman.status === 'dalam_pengiriman' ? 'Menunggu penerimaan di toko...' : 'Tidak ada catatan penerima.'}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                          {/* 2. Tab List Item (Tabel) */}
                          {modalTab === 'list' && (
                            <div className="space-y-3">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div className="relative flex-1 max-w-xs">
                                  <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                                  <input
                                    type="text"
                                    placeholder="Cari kode, jenis, baki..."
                                    value={searchModalItem}
                                    onChange={(e) => setSearchModalItem(e.target.value)}
                                    className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200 text-slate-800 dark:text-slate-100"
                                  />
                                </div>
                                <span className="text-[11px] text-slate-500 font-medium">
                                  Menampilkan {filteredModalItems.length} dari {selectedPengiriman.items?.length || 0} item
                                </span>
                              </div>

                              <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto max-h-80">
                                <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                                  <thead className="bg-gradient-to-r from-amber-50 via-amber-100/80 to-amber-50 dark:from-amber-900/40 dark:via-amber-800/30 dark:to-amber-900/40 text-[11px] text-amber-800 dark:text-amber-300 uppercase border-b border-amber-200/70 dark:border-amber-800/40 sticky top-0 z-10 font-bold tracking-wider">
                                    <tr>
                                      <th className="p-2.5 w-10 text-center font-bold">No</th>
                                      <th className="p-2.5 font-bold">Kode Barang</th>
                                      <th className="p-2.5 font-bold">Nama Barang</th>
                                      <th className="p-2.5 text-center font-bold">Baki / Rak</th>
                                      <th className="p-2.5 text-right font-bold">Berat (gr)</th>
                                      <th className="p-2.5 text-center font-bold">Karat</th>
                                      <th className="p-2.5 text-center font-bold">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                                    {filteredModalItems.length === 0 ? (
                                      <tr>
                                        <td colSpan={7} className="text-center py-6 text-slate-400">
                                          {loadingDetail ? 'Memuat daftar item...' : 'Tidak ada item yang sesuai dengan pencarian'}
                                        </td>
                                      </tr>
                                    ) : (
                                      filteredModalItems.map((it, idx) => (
                                        <tr key={it.id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                                          <td className="p-2.5 text-slate-500 font-medium text-center">{idx + 1}.</td>
                                          <td className="p-2.5 font-mono font-bold text-amber-600 dark:text-amber-400">{it.kode_item}</td>
                                          <td className="p-2.5">
                                            <span className="text-slate-700 dark:text-slate-200 font-medium">
                                              {it.jenis_kategori?.kategori?.nama_kategori ? `${it.jenis_kategori.kategori.nama_kategori} - ${it.jenis_kategori.nama_jenis || ''}` : it.nama_barang || '-'}
                                            </span>
                                          </td>
                                          <td className="p-2.5 text-center font-medium">{it.nomor_rak || '-'}</td>
                                          <td className="p-2.5 text-right font-bold text-amber-600 dark:text-amber-400">
                                            {parseFloat(it.berat || 0).toFixed(3)} gr
                                          </td>
                                          <td className="p-2.5 text-center font-semibold">{it.karat ? `${it.karat} K` : '-'}</td>
                                          <td className="p-2.5 text-center">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                              it.pivot?.status_item === 'diterima' || selectedPengiriman.status === 'diterima'
                                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                                                : it.pivot?.status_item === 'ditolak' || selectedPengiriman.status === 'ditolak'
                                                ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'
                                                : 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300'
                                            }`}>
                                              {it.pivot?.status_item || (selectedPengiriman.status === 'dalam_pengiriman' ? 'Pengiriman' : selectedPengiriman.status)}
                                            </span>
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                  {filteredModalItems.length > 0 && (
                                    <tfoot className="bg-slate-50 dark:bg-slate-700/50 font-bold border-t border-slate-200 dark:border-slate-700 sticky bottom-0">
                                      <tr>
                                        <td colSpan={4} className="p-2.5 text-right text-slate-600 dark:text-slate-300">
                                          Total ({filteredModalItems.length} item):
                                        </td>
                                        <td className="p-2.5 text-right text-amber-600 dark:text-amber-400 font-mono">
                                          {filteredModalItems.reduce((acc, it) => acc + parseFloat(it.berat || 0), 0).toFixed(3)} gr
                                        </td>
                                        <td colSpan={2}></td>
                                      </tr>
                                    </tfoot>
                                  )}
                                </table>
                              </div>
                            </div>
                          )}

                          {/* 3. Tab Operator / Pembuat Pengiriman */}
                          {modalTab === 'operator' && (() => {
                            const userPembuat = selectedPengiriman.pemberi_user || selectedPengiriman.pemberiUser;
                            return (
                              <div className="space-y-4">
                                <div className="bg-slate-50 dark:bg-slate-700/30 p-5 rounded-2xl border border-slate-200 dark:border-slate-700">
                                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-600">
                                    <div className="flex items-center gap-3.5">
                                      <div className="w-12 h-12 rounded-2xl bg-amber-500/10 dark:bg-amber-400/20 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-lg border border-amber-200 dark:border-amber-700/50">
                                        {userPembuat?.name ? userPembuat.name.charAt(0).toUpperCase() : <IconUser size={24} />}
                                      </div>
                                      <div>
                                        <h4 className="font-bold text-base text-slate-800 dark:text-slate-100">
                                          {userPembuat?.name || 'Operator Pusat / Pembuat'}
                                        </h4>
                                        {userPembuat?.email && (
                                          <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {userPembuat.email}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                                        {userPembuat?.roles?.map((r) => (typeof r === 'string' ? r : r.name).replace(/_/g, ' ').toUpperCase()).join(', ') || 'OPERATOR PUSAT'}
                                      </span>
                                      <span className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border ${
                                        userPembuat?.is_active !== false && userPembuat?.is_active !== 0
                                          ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-200'
                                      }`}>
                                        {userPembuat?.is_active !== false && userPembuat?.is_active !== 0 ? '● Akun Aktif' : '○ Non-Aktif'}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 text-xs">
                                    <div className="space-y-1">
                                      <span className="text-slate-400 text-[11px] block">Cabang / Lokasi Asal</span>
                                      <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-200">
                                        <IconBuildingStore size={15} className="text-amber-500" />
                                        <span>{selectedPengiriman.cabang_asal?.nama_cabang || userPembuat?.cabang?.nama_cabang || 'Brankas Pusat'}</span>
                                      </div>
                                    </div>

                                    <div className="space-y-1">
                                      <span className="text-slate-400 text-[11px] block">Toko Tujuan Pengiriman</span>
                                      <div className="flex items-center gap-1.5 font-bold text-amber-600 dark:text-amber-400">
                                        <IconBuildingStore size={15} className="text-amber-500" />
                                        <span>{selectedPengiriman.cabang_tujuan?.nama_cabang || '-'}</span>
                                      </div>
                                    </div>

                                    <div className="space-y-1">
                                      <span className="text-slate-400 text-[11px] block">Kurir Pengantar / Supir</span>
                                      <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-200">
                                        <IconTruckDelivery size={15} className="text-amber-500" />
                                        <span>{selectedPengiriman.nama_kurir || '-'}</span>
                                      </div>
                                    </div>

                                    <div className="space-y-1 sm:col-span-2 md:col-span-3 pt-2 border-t border-slate-200 dark:border-slate-600">
                                      <span className="text-slate-400 text-[11px] block">Catatan yang Dilampirkan Pembuat</span>
                                      <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                                        {selectedPengiriman.catatan_pengiriman || <span className="italic text-slate-400">Tidak ada catatan pengiriman dari pembuat.</span>}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* 4. Tab Penerima / Validasi Toko */}
                          {modalTab === 'verifikator' && (() => {
                            const userPenerima = selectedPengiriman.penerima_user || selectedPengiriman.penerimaUser;
                            return (
                              <div className="space-y-4">
                                {userPenerima ? (
                                  <div className="bg-slate-50 dark:bg-slate-700/30 p-5 rounded-2xl border border-slate-200 dark:border-slate-700">
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-600">
                                      <div className="flex items-center gap-3.5">
                                        <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 dark:bg-emerald-400/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-lg border border-emerald-200 dark:border-emerald-700/50">
                                          {userPenerima.name ? userPenerima.name.charAt(0).toUpperCase() : <IconCircleCheck size={24} />}
                                        </div>
                                        <div>
                                          <h4 className="font-bold text-base text-slate-800 dark:text-slate-100">
                                            {userPenerima.name}
                                          </h4>
                                          {userPenerima?.email && (
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                              {userPenerima.email}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                          {userPenerima.roles?.map((r) => (typeof r === 'string' ? r : r.name).replace(/_/g, ' ').toUpperCase()).join(', ') || 'KASIR / TOKO'}
                                        </span>
                                        <span className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border ${
                                          selectedPengiriman.status === 'diterima'
                                            ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700'
                                            : 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-700'
                                        }`}>
                                          {selectedPengiriman.status === 'diterima' ? '✓ Diterima Toko' : '✕ Ditolak'}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 text-xs">
                                      <div className="space-y-1">
                                        <span className="text-slate-400 text-[11px] block">Unit / Toko Penerima</span>
                                        <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-200">
                                          <IconBuildingStore size={15} className="text-emerald-500" />
                                          <span>{selectedPengiriman.cabang_tujuan?.nama_cabang || userPenerima.cabang?.nama_cabang || '-'}</span>
                                        </div>
                                      </div>

                                      <div className="space-y-1">
                                        <span className="text-slate-400 text-[11px] block">Waktu Penerimaan Selesai</span>
                                        <div className="flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-300">
                                          <IconClock size={15} className="text-emerald-500" />
                                          <span>{selectedPengiriman.waktu_diterima ? moment(selectedPengiriman.waktu_diterima).format('DD MMM YYYY, HH:mm:ss') : '-'}</span>
                                        </div>
                                      </div>

                                      <div className="space-y-1">
                                        <span className="text-slate-400 text-[11px] block">Status Akun Penerima</span>
                                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                          {userPenerima.is_active !== false && userPenerima.is_active !== 0 ? '● Aktif' : '○ Non-Aktif'}
                                        </span>
                                      </div>

                                      <div className="space-y-1 sm:col-span-2 md:col-span-3 pt-2 border-t border-slate-200 dark:border-slate-600">
                                        <span className="text-slate-400 text-[11px] block">Catatan / Keterangan Penerimaan Toko</span>
                                        <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                                          {selectedPengiriman.catatan_penerimaan || <span className="italic text-slate-400">Tidak ada catatan fisik atau selisih dari penerima toko.</span>}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-8 text-center bg-amber-50/50 dark:bg-amber-900/10 rounded-2xl border border-amber-200/60 dark:border-amber-900/30 space-y-3">
                                    <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                                      <IconClock size={24} />
                                    </div>
                                    <h4 className="font-bold text-sm text-amber-800 dark:text-amber-300">Menunggu Penerimaan & Validasi Toko</h4>
                                    <p className="text-xs text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                                      Dokumen surat jalan ini berstatus <strong className="text-amber-700 dark:text-amber-400">Dalam Pengiriman</strong> oleh kurir (<strong>{selectedPengiriman.nama_kurir}</strong>) ke <strong>{selectedPengiriman.cabang_tujuan?.nama_cabang}</strong>. Informasi akun validator toko akan otomatis terisi setelah staf toko tujuan memeriksa fisik dan menekan tombol validasi terima.
                                    </p>
                                    {selectedPengiriman.status === 'dalam_pengiriman' && (
                                      <div className="pt-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            handleOpenValidation(selectedPengiriman);
                                          }}
                                          className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow transition-all inline-flex items-center gap-1.5 cursor-pointer"
                                        >
                                          <IconCheck size={16} />
                                          <span>Validasi Serah Terima Toko Sekarang</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* 5. Tab Galeri List Item */}
                          {modalTab === 'galeri' && (
                            <div className="space-y-4">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div className="relative flex-1 max-w-xs">
                                  <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                                  <input
                                    type="text"
                                    placeholder="Cari kode, jenis, baki..."
                                    value={searchModalItem}
                                    onChange={(e) => setSearchModalItem(e.target.value)}
                                    className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200 text-slate-800 dark:text-slate-100"
                                  />
                                </div>
                                <span className="text-[11px] text-slate-500 font-medium">
                                  Menampilkan {filteredModalItems.length} foto item
                                </span>
                              </div>

                              {filteredModalItems.length === 0 ? (
                                <div className="py-12 text-center text-slate-400">
                                  <IconInbox size={36} className="mx-auto mb-2 opacity-50" />
                                  <p className="text-xs">
                                    {loadingDetail ? 'Memuat foto barang...' : 'Tidak ada item yang sesuai dengan pencarian'}
                                  </p>
                                </div>
                              ) : (
                                <PhotoProvider>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-96 overflow-y-auto p-1">
                                    {filteredModalItems.map((item, idx) => {
                                      const imgUrl = resolveItemImageUrl(item);
                                      return (
                                        <div
                                          key={item.id || idx}
                                          className="group bg-slate-50 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col"
                                        >
                                          <div className="relative aspect-square w-full bg-slate-200 dark:bg-slate-800 overflow-hidden flex items-center justify-center">
                                            {imgUrl ? (
                                              <PhotoView src={imgUrl}>
                                                <img
                                                  src={imgUrl}
                                                  alt={item.kode_item}
                                                  className="w-full h-full object-cover cursor-zoom-in group-hover:scale-105 transition-transform duration-300"
                                                  onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    e.currentTarget.parentElement.innerHTML = '<div class="flex flex-col items-center justify-center text-slate-400 w-full h-full"><svg class="w-8 h-8 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg><span class="text-[10px] mt-1">Foto Rusak</span></div>';
                                                  }}
                                                />
                                              </PhotoView>
                                            ) : (
                                              <div className="flex flex-col items-center justify-center text-slate-400 p-4">
                                                <IconPhoto size={32} className="opacity-40" />
                                                <span className="text-[10px] mt-1">Tanpa Foto</span>
                                              </div>
                                            )}
                                            <div className="absolute top-1.5 left-1.5 bg-black/60 backdrop-blur-sm text-white text-[10px] font-mono font-bold px-2 py-0.5 rounded">
                                              {item.kode_item}
                                            </div>
                                          </div>

                                          <div className="p-2.5 flex-1 flex flex-col justify-between text-xs">
                                            <div>
                                              <p className="font-bold text-slate-800 dark:text-slate-100 truncate">
                                                {item.jenis_kategori?.kategori?.nama_kategori ? `${item.jenis_kategori.kategori.nama_kategori} - ${item.jenis_kategori.nama_jenis || ''}` : item.nama_barang || '-'}
                                              </p>
                                              <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                                Baki: {item.nomor_rak || '-'}
                                              </p>
                                            </div>
                                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200 dark:border-slate-600/50">
                                              <span className="font-bold text-amber-600 dark:text-amber-400 font-mono">
                                                {parseFloat(item.berat || 0).toFixed(3)} gr
                                              </span>
                                              <span className="font-semibold text-slate-700 dark:text-slate-300">
                                                {item.karat ? `${item.karat} K` : '-'}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </PhotoProvider>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Modal Footer */}
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-100 dark:border-slate-700 flex-shrink-0">
                          <div className="text-xs text-slate-500 flex items-center gap-1.5">
                            <span className="font-medium">Total: <strong>{selectedPengiriman.total_pcs || (selectedPengiriman.items?.length || 0)} Pcs</strong></span>
                            <span>•</span>
                            <span className="font-mono text-amber-600 dark:text-amber-400 font-bold">
                              {parseFloat(selectedPengiriman.total_berat || 0).toFixed(3)} gr
                            </span>
                          </div>

                          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                            <button
                              type="button"
                              onClick={() => handleOpenPrint(selectedPengiriman.id)}
                              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-semibold text-xs rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
                            >
                              <IconPrinter size={16} />
                              <span>Cetak Surat Jalan</span>
                            </button>

                            {selectedPengiriman.status === 'dalam_pengiriman' && (
                              <button
                                type="button"
                                onClick={() => handleOpenValidation(selectedPengiriman)}
                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-1.5 cursor-pointer"
                              >
                                <IconCheck size={16} />
                                <span>Validasi Toko</span>
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => setIsOpenDetailModal(false)}
                              className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all cursor-pointer"
                            >
                              Tutup
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Printable Component Modal */}
        <SuratJalanPrint
          isOpen={isOpenPrint}
          closeModal={() => setIsOpenPrint(false)}
          data={selectedPengiriman}
          breakdown={selectedBreakdown}
        />
      </div>
    </Layouts>
  );
};

export default HistoriPengiriman;
